import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { SlColumn, SlRow, StacklineColor, useStacklineTheme } from '@stackline/ui';
import queryString from 'qs';
import React, { useState } from 'react';
import { Segment } from 'sl-api-connector';
import ElementCard from 'src/components/BeaconV2Components/ElementCard/ElementCard';
import GenericCardLoading from 'src/components/BeaconV2Components/GenericCardLoading/GenericCardLoading';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import {
  getColorForMetricChangeValue,
  getIconForMetricChange
} from 'src/components/BeaconV2Components/utils/chartStyles';
import { BEACON_PRO_ENTITY_CARD_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { getAppName, getAtlasSalesV2Treatment } from 'src/utils/app';
import { AppName } from 'sl-api-connector';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { extractQueryConditions } from 'src/utils/segments';
import { SegmentMiniButton, TopicsMiniButton } from './MiniButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { useAtlasSegmentData } from './hooks/useAtlasSegmentData';
import { useMetricByWeekId } from 'src/serverProxy/useMetricByWeekId';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { useCallback } from 'react';
import { ATLAS_SUBTABS, ATLAS_TABS } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import { Link } from 'react-router-dom';
import { width } from '@mui/system';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

interface SegmentCardProps {
  percentChange: number;
  displayName: string;
  subtitle: string;
  metricTotal: string | null;
  primaryData: number[][];
  secondaryData: number[][];
  segmentId: string;
  savedSearch: Segment['segment'];
  segmentUserId: string;
  metricType: METRICTYPE;
  handleDelete: () => void;
  metricDisplayName?: string;
  color?: StacklineColor;
  isTopicSegment?: boolean;
  isPending?: boolean;
  isLoading?: boolean;
}

const SegmentCardTitle = styled('p')(() => ({
  whiteSpace: 'pre-wrap',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '3',
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 500,
  fontSize: '18px',
  margin: 0
}));

const SegmentCard = React.memo(
  ({
    segmentId,
    percentChange,
    displayName,
    subtitle,
    metricTotal,
    primaryData,
    secondaryData,
    savedSearch,
    segmentUserId,
    metricType,
    handleDelete,
    metricDisplayName = 'Retail Sales',
    isTopicSegment = false,
    isPending = false,
    isLoading = false
  }: SegmentCardProps) => {
    const [hovered, setHovered] = useState(false);
    const theme = useStacklineTheme();
    const formatMetric = useMetricFormatter();
    const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
    const { userId } = useAppSelector((state) => state.user.session);

    // Show loading UI when either pending or loading data
    const showLoadingUI = isPending || isLoading;
    // const showLoadingUI = true;

    // Beacon will have their own Topic card
    const getSegmentTab = (): string => {
      if (isTopicSegment) {
        return ATLAS_TABS.TRAFFIC;
      }

      if (getAtlasSalesV2Treatment() === 'off') {
        return getAppName() === AppName.Beacon ? BEACON_TABS.SUMMARY : ATLAS_TABS.SUMMARY;
      }

      return ATLAS_TABS.SUMMARY_V2;
    };

    const getSegmentSubtab = (): string => {
      if (getAppName() === AppName.Beacon) {
        return BEACON_SUBTABS.KEY_METRICS;
      }

      if (isTopicSegment) {
        return ATLAS_SUBTABS.TOTAL_CLICKS;
      }

      return 'all';
    };

    const buildSegmentUrl = (): string => {
      const basePath = isTopicSegment ? 'searchtermlist' : 'segment';
      const tab = getSegmentTab();
      const subtab = getSegmentSubtab();

      return `/${basePath}/${segmentId}${searchParams}&tab=${tab}&subtab=${subtab}`;
    };

    return (
      <div
        style={{
          width: BEACON_PRO_ENTITY_CARD_WIDTH,
          height: '484px',
          border: `1px solid ${theme.colors.primaryGray}`,
          borderRadius: theme.spacing.sm,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          position: 'relative'
        }}
        onMouseEnter={() => !showLoadingUI && setHovered(true)}
        onMouseLeave={() => !showLoadingUI && setHovered(false)}
      >
        {isPending && (
          <Flex
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: '#FFF8EA',
              padding: `4px 20px`,
              borderRadius: '14px',
              width: '92px',
              height: '28px',
              alignItems: 'center',
              cursor: 'default'
            }}
          >
            <Text variant="subtitle2" color="primary">
              Pending
            </Text>
          </Flex>
        )}
        <Box>
          <Box padding={theme.spacing.md} display="flex" flexDirection="column" gap={theme.spacing.md}>
            <SlRow horizontalPosition="space-between" verticalPosition="center">
              <ElementCard
                textSx={{ fontSize: '20px', fontWeight: 'bold' }}
                color={isTopicSegment ? 'accentTangerine' : 'info'}
                variant="small"
              >
                {`${(displayName[0] || '').toUpperCase()}${(displayName[1] || '').toLowerCase()}`}
              </ElementCard>
              {hovered && !showLoadingUI && (
                <SlRow spacing="sm">
                  {userId === segmentUserId && (
                    <Link
                      to={`/search${searchParams}&doAggregation=true&entityType=${
                        savedSearch.entityType
                      }&${queryString.stringify(extractQueryConditions(savedSearch, true, true))}&action=edit`}
                    >
                      {isTopicSegment ? (
                        <TopicsMiniButton>Edit</TopicsMiniButton>
                      ) : (
                        <SegmentMiniButton>Edit</SegmentMiniButton>
                      )}
                    </Link>
                  )}
                  {userId === segmentUserId && (
                    <Text onClick={() => handleDelete()}>
                      {isTopicSegment ? (
                        <TopicsMiniButton>Delete</TopicsMiniButton>
                      ) : (
                        <SegmentMiniButton>Delete</SegmentMiniButton>
                      )}
                    </Text>
                  )}
                  <Link
                    to={`/search${searchParams}&entityType=${savedSearch.entityType}&${queryString.stringify(
                      extractQueryConditions(savedSearch, true)
                    )}&action=create`}
                  >
                    {isTopicSegment ? (
                      <TopicsMiniButton>Duplicate</TopicsMiniButton>
                    ) : (
                      <SegmentMiniButton>Duplicate</SegmentMiniButton>
                    )}
                  </Link>
                </SlRow>
              )}
            </SlRow>
            <Box display="flex" flexDirection="column" gap={theme.spacing.xs}>
              {/* This getAppName() === AppName.Atlas temp flag until we implement doris for topics */}
              {showLoadingUI && getAppName() === AppName.Atlas && !isTopicSegment ? (
                <div style={{ cursor: 'default' }}>
                  <SegmentCardTitle title={displayName}>{displayName}</SegmentCardTitle>
                </div>
              ) : (
                <Link to={buildSegmentUrl()}>
                  <SegmentCardTitle title={displayName}>{displayName}</SegmentCardTitle>
                </Link>
              )}
              <Text variant="body2">{subtitle}</Text>
            </Box>
          </Box>
          <Box sx={{ marginTop: '40px' }}>
            {showLoadingUI ? (
              <div style={{ width: '100%', height: 115 }} />
            ) : (
              <SplineChart
                metricType={metricType}
                primaryData={primaryData}
                secondaryData={secondaryData}
                removeSpacing
                preview={{ width: +BEACON_PRO_ENTITY_CARD_WIDTH.replace('px', ''), height: 115 }}
                chartOptionsOverride={{
                  chart: {
                    marginLeft: -2
                  },
                  plotOptions: {
                    series: {
                      lineWidth: 2
                    }
                  }
                }}
              />
            )}
          </Box>
        </Box>
        <div>
          <SlColumn spacing="xxs" verticalInset="md" horizontalInset="md">
            <Text
              sx={{ display: 'flex', height: '24px', alignItems: 'center' }}
              variant="subtitle2"
              transform="uppercase"
            >
              {metricDisplayName}
            </Text>
            {showLoadingUI ? (
              <SlSkeleton variant="text" width={120} height={20} />
            ) : (
              <SlRow verticalPosition="center" spacing="sm">
                <Text variant="body1">{metricTotal}</Text>
                <SlRow verticalPosition="center" spacing="xs">
                  {getIconForMetricChange(percentChange, 2)}
                  <Text variant="subtitle2" color={getColorForMetricChangeValue(percentChange)}>
                    {`${formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, {
                      decimalPlaces: 2,
                      showNegative: false
                    })}`}
                  </Text>
                </SlRow>
              </SlRow>
            )}
          </SlColumn>
        </div>
      </div>
    );
  }
);

SegmentCard.displayName = 'SegmentCard';

interface SmartSegmentCardProps {
  segment: Segment;
  indexName?: string;
  fieldName?: string;
  metricDisplayName?: string;
  color?: StacklineColor;
  isTopicSegment?: boolean;
  isPending?: boolean;
  handleDelete?: () => void;
}

/**
 * Atlas-specific segment card component that uses GetSalesMetrics API
 * Following the same pattern as VariationsSplineChart
 */
export const AtlasSmartSegmentCard = ({
  segment,
  fieldName = 'retailSales',
  metricDisplayName = 'Retail Sales',
  handleDelete,
  isTopicSegment,
  isPending = false
}: SmartSegmentCardProps) => {
  const { adapter, isLoading, hasValidData } = useAtlasSegmentData({ segment, fieldName, isPending });

  // Show loading UI when either pending or loading data
  const showLoadingUI = isPending || isLoading;

  // For loading/pending segments, use default values
  let chartData = { primaryData: [], secondaryData: [] };
  let legendData = {
    percentChange: 0,
    formattedPrimaryTotal: null,
    metricType: METRICTYPE.VOLUME
  };

  // Only transform data if not loading/pending and has valid data
  if (!showLoadingUI && hasValidData) {
    const transformedData = adapter.transform();
    chartData = transformedData.chartData;
    legendData = transformedData.legendData;
  }

  return (
    <SegmentCard
      savedSearch={segment?.segment || null}
      segmentId={segment?.id || ''}
      displayName={segment?.displayName || 'Unknown Segment'}
      percentChange={legendData.percentChange}
      subtitle={`Created by ${segment?.segment?.owner?.firstName || 'Unknown'} ${
        segment?.segment?.owner?.lastName || 'User'
      }`}
      metricTotal={legendData.formattedPrimaryTotal}
      primaryData={chartData.primaryData}
      secondaryData={chartData.secondaryData}
      segmentUserId={segment?.userId || ''}
      metricDisplayName={metricDisplayName}
      metricType={legendData.metricType}
      isTopicSegment={isTopicSegment}
      isPending={isPending}
      isLoading={isLoading}
      handleDelete={handleDelete}
    />
  );
};

/**
 * Beacon-specific segment card component that uses AdvancedSearch API
 * Following the original working pattern exactly, but will eventually be deprecated as we are moving from Elasticsearch to Doris
 */
export const BeaconSmartSegmentCard = React.memo(
  ({
    segment,
    indexName = 'sales',
    fieldName = 'retailSales',
    metricDisplayName = 'Retail Sales',
    handleDelete,
    isTopicSegment,
    isPending = false
  }: SmartSegmentCardProps) => {
    const requestBuilder = useCallback(
      (body: AdvancedSearchRequestBuilder) => {
        body.clearConditionRangeFilters().clearConditionTermFilters();

        if (segment.conditions.rangeFilters) {
          segment.conditions.rangeFilters.forEach((rangeFilter) => {
            body.addConditionRangeFilter(rangeFilter.fieldName, rangeFilter.minValue, rangeFilter.maxValue);
          });
        }

        if (segment.conditions.termFilters) {
          segment.conditions.termFilters.forEach((termFilter) => {
            body.addConditionTermFilter(termFilter.fieldName, termFilter.condition, termFilter.values);
          });
        }
        return body;
      },
      [segment]
    );

    const {
      formattedPrimaryTotal,
      percentChange,
      isLoading,
      primaryData,
      secondaryData,
      field: { metricType }
    } = useMetricByWeekId({
      indexName,
      fieldName,
      requestBuilder
    });

    // Show loading UI when either pending or loading data
    const showLoadingUI = isPending || isLoading;

    // For loading/pending segments, use default values
    const displayData = showLoadingUI
      ? {
          formattedPrimaryTotal: null,
          percentChange: 0,
          primaryData: [],
          secondaryData: [],
          metricType: metricType || METRICTYPE.VOLUME
        }
      : {
          formattedPrimaryTotal,
          percentChange,
          primaryData,
          secondaryData,
          metricType
        };

    return (
      <SegmentCard
        savedSearch={segment.segment}
        segmentId={segment.id}
        displayName={segment.displayName}
        percentChange={displayData.percentChange}
        subtitle={`Created by ${segment.segment.owner.firstName} ${segment.segment.owner.lastName}`}
        metricTotal={displayData.formattedPrimaryTotal}
        primaryData={displayData.primaryData}
        secondaryData={displayData.secondaryData}
        segmentUserId={segment?.userId || ''}
        metricDisplayName={metricDisplayName}
        metricType={displayData.metricType}
        isTopicSegment={isTopicSegment}
        isPending={isPending}
        isLoading={isLoading}
        handleDelete={handleDelete}
      />
    );
  }
);

BeaconSmartSegmentCard.displayName = 'BeaconSmartSegmentCard';

/**
 * Smart wrapper that determines which segment card implementation to use
 * - Atlas: Uses GetSalesMetrics API (new)
 * - Beacon: Uses AdvancedSearch API (legacy, will be deprecated)
 */
export const SmartSegmentCard = React.memo((props: SmartSegmentCardProps) => {
  const isAtlasDoris = getAppName() === AppName.Atlas && !props.isTopicSegment;

  if (isAtlasDoris) {
    return <AtlasSmartSegmentCard {...props} />;
  } else {
    return <BeaconSmartSegmentCard {...props} />;
  }
});

SmartSegmentCard.displayName = 'SmartSegmentCard';

export default SegmentCard;
